
import { useMemo } from 'react';
import { Attendee } from '@/types/attendee';

// T-shirt size prefixes for categorization
const MALE_PREFIX = "Nam_";
const FEMALE_PREFIX = "Nu_";

// Define the TShirtStats type and export it
export interface TShirtStats {
  // Male sizes
  maleXS: number;
  maleS: number;
  maleM: number;
  maleL: number;
  maleXL: number;
  male2XL: number;
  male3XL: number;
  male4XL: number;
  
  // Female sizes
  femaleXS: number;
  femaleS: number;
  femaleM: number;
  femaleL: number;
  femaleXL: number;
  female2XL: number;
  
  // Totals
  totalMale: number;
  totalFemale: number;
  total: number;
  unknown: number;
  
  // Distribution data
  totalDistributed: number;
  totalSized: number;
  totalRemaining: number;
  
  // Formatted data for UI rendering
  menSizes: Record<string, number>;
  womenSizes: Record<string, number>;
  distributedMenSizes: Record<string, number>;
  distributedWomenSizes: Record<string, number>;
  
  // Calculated totals
  totalMen: number;
  totalWomen: number;
}

/**
 * Hook for calculating T-shirt statistics from attendee data
 */
export const useTShirtStats = (attendees: Attendee[]): TShirtStats => {
  return useMemo(() => {
    // Initialize counters
    const sizeCounts = {
      // Male sizes
      maleXS: 0,
      maleS: 0,
      maleM: 0,
      maleL: 0,
      maleXL: 0,
      male2XL: 0,
      male3XL: 0,
      male4XL: 0,
      
      // Female sizes
      femaleXS: 0,
      femaleS: 0,
      femaleM: 0,
      femaleL: 0,
      femaleXL: 0,
      female2XL: 0,
      
      // Totals
      totalMale: 0,
      totalFemale: 0,
      total: 0,
      unknown: 0,
      
      // Additional tracking for distribution status
      totalDistributed: 0,
      
      // Organized data for UI display
      menSizes: {} as Record<string, number>,
      womenSizes: {} as Record<string, number>,
      distributedMenSizes: {} as Record<string, number>,
      distributedWomenSizes: {} as Record<string, number>
    };
    
    // Count sizes
    attendees.forEach(attendee => {
      const size = attendee.t_shirt_size;
      // Check if attended is true to determine if T-shirt was distributed
      // Assuming that T-shirts are distributed when attendees check in
      const isDistributed = attendee.attended === true;
      
      if (!size) {
        sizeCounts.unknown++;
        return;
      }
      
      // Track distribution status
      if (isDistributed) {
        sizeCounts.totalDistributed++;
      }
      
      if (size.startsWith(MALE_PREFIX)) {
        // Extract size without prefix
        const sizeWithoutPrefix = size.substring(MALE_PREFIX.length);
        sizeCounts.totalMale++;
        
        // Add to men's size object for UI display
        if (!sizeCounts.menSizes[sizeWithoutPrefix]) {
          sizeCounts.menSizes[sizeWithoutPrefix] = 0;
        }
        sizeCounts.menSizes[sizeWithoutPrefix]++;
        
        // Track distributed items by size
        if (isDistributed) {
          if (!sizeCounts.distributedMenSizes[sizeWithoutPrefix]) {
            sizeCounts.distributedMenSizes[sizeWithoutPrefix] = 0;
          }
          sizeCounts.distributedMenSizes[sizeWithoutPrefix]++;
        }
        
        // Increment specific size counter
        switch (sizeWithoutPrefix) {
          case 'XS': sizeCounts.maleXS++; break;
          case 'S': sizeCounts.maleS++; break;
          case 'M': sizeCounts.maleM++; break;
          case 'L': sizeCounts.maleL++; break;
          case 'XL': sizeCounts.maleXL++; break;
          case '2XL': sizeCounts.male2XL++; break;
          case '3XL': sizeCounts.male3XL++; break;
          case '4XL': sizeCounts.male4XL++; break;
          default: sizeCounts.unknown++;
        }
      } 
      else if (size.startsWith(FEMALE_PREFIX)) {
        // Extract size without prefix
        const sizeWithoutPrefix = size.substring(FEMALE_PREFIX.length);
        sizeCounts.totalFemale++;
        
        // Add to women's size object for UI display
        if (!sizeCounts.womenSizes[sizeWithoutPrefix]) {
          sizeCounts.womenSizes[sizeWithoutPrefix] = 0;
        }
        sizeCounts.womenSizes[sizeWithoutPrefix]++;
        
        // Track distributed items by size
        if (isDistributed) {
          if (!sizeCounts.distributedWomenSizes[sizeWithoutPrefix]) {
            sizeCounts.distributedWomenSizes[sizeWithoutPrefix] = 0;
          }
          sizeCounts.distributedWomenSizes[sizeWithoutPrefix]++;
        }
        
        // Increment specific size counter
        switch (sizeWithoutPrefix) {
          case 'XS': sizeCounts.femaleXS++; break;
          case 'S': sizeCounts.femaleS++; break;
          case 'M': sizeCounts.femaleM++; break;
          case 'L': sizeCounts.femaleL++; break;
          case 'XL': sizeCounts.femaleXL++; break;
          case '2XL': sizeCounts.female2XL++; break;
          default: sizeCounts.unknown++;
        }
      } 
      else {
        sizeCounts.unknown++;
      }
    });
    
    // Calculate total
    sizeCounts.total = sizeCounts.totalMale + sizeCounts.totalFemale + sizeCounts.unknown;
    
    // Calculate sized total (those with valid sizes)
    const totalSized = sizeCounts.totalMale + sizeCounts.totalFemale;
    
    // Calculate remaining shirts (not distributed yet)
    const totalRemaining = totalSized - sizeCounts.totalDistributed;
    
    return {
      ...sizeCounts,
      totalSized,
      totalRemaining,
      totalMen: sizeCounts.totalMale,
      totalWomen: sizeCounts.totalFemale
    };
  }, [attendees]);
};
